import {
  AnalyticsActionConsentEvent,
  AnalyticsEventParam,
  ConsentContext,
  PluginType,
  PrivacyApi,
  PrivacyEvent,
} from '@accedo/one-plugin-sdk';
import { ErrorBoundary } from '@dynamic/ui';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { userService } from '#/client';
import { useGlobalContext } from '#/components/useGlobalContext';
import { useConfig } from '#/hooks';
import { ignoreError } from '#/service/utils';

import pluginManager from '../pluginManager';
import { privacyStoreActions } from './privacyStore';

const onError = (...errorDetails) => {
  console.error('Privacy plugin error', ...errorDetails);
};

const PrivacyManager = () => {
  const {
    boot: { isPluginManagerReady },
  } = useGlobalContext();
  const { features } = useConfig();
  const loaded = useRef<boolean>(false);

  useEffect(() => {
    return () => {
      privacyStoreActions.reset();
    };
  }, []);

  // Get the first enabled ad feature config
  const enabledPrivacyConfig = useMemo(
    () => features?.privacy?.find((privacy) => privacy.enabled),
    [features]
  );

  const handleSaveRef = useCallback(
    (apiRef) => {
      if (!enabledPrivacyConfig || loaded.current) {
        return;
      }
      privacyStoreActions.save({
        apiRef,
        properties: enabledPrivacyConfig.properties,
      });
      loaded.current = true;
      privacyStoreActions.addEventListener(
        PrivacyEvent.Loaded,
        (data: ConsentContext) => {
          privacyStoreActions.saveConsents(data);
          privacyStoreActions.toggleFloatingButton(
            !enabledPrivacyConfig.properties.disableFloatingButton
          );
        }
      );
      privacyStoreActions.addEventListener(PrivacyEvent.Error, onError);
      privacyStoreActions.addEventListener(
        PrivacyEvent.ConsentUpdated,
        (data: ConsentContext) => {
          privacyStoreActions.saveConsents(data);

          // Only notify core if we are handling gdpr consents
          if (data?.gdpr) {
            userService.refresh().catch(ignoreError);
          }
          document.dispatchEvent(
            new AnalyticsActionConsentEvent({
              [AnalyticsEventParam.Consent]: data,
            })
          );
        }
      );
    },
    [enabledPrivacyConfig]
  );

  if (!enabledPrivacyConfig || !isPluginManagerReady) {
    return null;
  }

  const PrivacyComponent = pluginManager.getPluginComponent<{
    apiRef?: React.Ref<PrivacyApi>;
  }>({
    type: PluginType.Privacy,
    id: enabledPrivacyConfig.plugin?.id,
    optional: true,
  });

  return (
    <ErrorBoundary key={enabledPrivacyConfig.key} onError={onError}>
      <Suspense fallback={null}>
        <PrivacyComponent apiRef={handleSaveRef} />
      </Suspense>
    </ErrorBoundary>
  );
};

export default PrivacyManager;
